<script setup lang="ts">
import { computed, h } from 'vue'
import { formatRiskScoreStr, scoreToLevel } from '../shared'
import type { RiskConfig, RiskShort } from '@/models/api-generated'
import RisksTooltip from '../RisksTooltip.vue'

const props = defineProps<{
	config?: RiskConfig
	average: number
}>()

const model = defineModel<RiskShort[]>({ required: true })

const levels = computed(() => {
	const config = props.config
	if (!config) return []
	return config.riskLevels.filter((_, i) => i <= config.matrixSize - 1)
})

const levelGroups = computed(() => {
	if (!props.config) return []
	const groups: RiskShort[][] = props.config.riskLevels.map(() => [])

	model.value.forEach((r) => {
		const lvl = scoreToLevel(r.residual, props.config)
		if (!lvl) return
		const idx = props.config!.riskLevels.findIndex((l) => l.title === lvl.title)
		if (idx >= 0) groups[idx].push(r)
	})

	return groups
})

const emit = defineEmits<{
	(e: 'go-to-risks', riskNames: string[], treatments: string[]): void
}>()

function goToRisks(names: string[]) {
	if (!names.length) return
	emit('go-to-risks', names, [])
}
</script>

<template>
	<div v-if="props.config" class="thermometer position-relative d-flex flex-row align-items-center w-100">
		<div class="text-center me-2">
			<i class="bi bi-thermometer-half fs-5"></i>
		</div>

		<div class="flex-fill d-flex bar-container position-relative">
			<div class="caret position-absolute" :style="`left: ${props.average * 100}%`">
				<div class="d-flex flex-column align-items-center lh-1">
					<small class="mb-0">{{ formatRiskScoreStr(props.average, 1, props.config) }}</small>
					<i class="bi bi-caret-down-fill fs-6 mt-0"></i>
				</div>
			</div>

			<div v-for="(l, i) in levels" :key="i" class="flex-chunk flex-fill d-flex flex-column align-items-center position-relative h-100">
				<div
					class="chunk w-100"
					:class="{ 'rounded-start': i === 0, 'rounded-end': i === props.config.matrixSize - 1 }"
					:style="'background-color: ' + l.color"
					v-one-tooltip="{
						content: () => h(RisksTooltip, { risks: levelGroups[i] }),
					}"
					@click="goToRisks(levelGroups[i].map((r) => r.name))"
				></div>
				<small class="count position-absolute">{{ levelGroups[i].length }}</small>
			</div>
		</div>

		<div class="text-center ms-2 fs-6">
			{{ props.config.matrixSize * props.config.matrixSize }}
		</div>
	</div>
</template>

<style lang="sass" scoped>
.thermometer
  max-width: 600px
  min-width: 200px
  height: auto

.bar-container
  height: 1em

.flex-chunk
  margin-left: .2em
  margin-right: .2em

.chunk
  flex: 1
  cursor: pointer

.count
  top: 100%
  left: 50%
  transform: translateX(-50%)

.caret
  bottom: 100%
  transform: translateX(-50%)
  margin-bottom: 0
</style>
